// Typed client for /api/workflow + a cached config context (stages/options/doctors/staff).
// Mutations that are safe to replay (moves, notes, holds, assignments…) go through the offline
// queue so a dropped Wi-Fi mid-action parks the write instead of losing it.
import { prepareUpload } from '../../lib/compressUpload.ts';
import { uploadWithProgress, type UploadStats } from '../../lib/uploadWithProgress.ts';
import type {
  CaseCompliance,
  ClinicOption,
  WfApprovalKind,
  CaseSetup,
  SetupAnnotation,
  SetupAnnotationInput,
  SetupView,
  ComplianceQueue,
  PendingPhoto,
  WfFile,
  WfInvoice,
  WfPatientSummary,
  WfScanBooking,
  WfScanSlot,
} from '@alignova/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode, createElement } from 'react';
import type {
  WfAgingReport,
  WfBankAccount,
  WfBankAccountInput,
  WfBillingSummary,
  WfBoardResponse,
  WfChargeInput,
  WfLedgerEntry,
  WfPayment,
  WfStatement,
  WfCase,
  WfCaseCreate,
  WfCaseListQuery,
  WfCaseListResponse,
  WfCasePatch,
  WfCaseProforma,
  WfCaseWorkspace,
  WfDashboard,
  WfDoctor,
  WfEvent,
  WfImportBatch,
  WfImportPreview,
  WfImportResult,
  WfNotification,
  WfOption,
  WfPortalMessage,
  WfPortalRequest,
  WfOptionKind,
  WfStage,
  WfStageInput,
  WfSummaryReport,
} from '@alignova/shared';
import { api, isDeferred, type Deferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';

export const WF = '/workflow';

export function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (Array.isArray(v)) {
      if (v.length) sp.set(k, v.join(','));
    } else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface WfConfig {
  stages: WfStage[];
  options: WfOption[];
  doctors: WfDoctor[];
  staff: Array<{ id: string; name: string; role: string }>;
}

export const wfApi = {
  config: () => api.get<WfConfig>(`${WF}/config`),
  saveStages: (stages: WfStageInput[]) => api.put<WfStage[]>(`${WF}/config/stages`, { stages }),
  upsertOption: (o: { kind: WfOptionKind; key: string; label: string; sortOrder?: number; isActive?: boolean; meta?: Record<string, unknown> | null }) =>
    api.post<WfOption>(`${WF}/config/options`, o),
  patchOption: (id: string, patch: Partial<Pick<WfOption, 'label' | 'sortOrder' | 'isActive' | 'meta'>>) => api.patch<WfOption>(`${WF}/config/options/${id}`, patch),
  /** Clinics for the intake picker, each carrying the doctor group it bills under. */
  clinics: (search?: string) => api.get<ClinicOption[]>(`${WF}/clinics${qs({ search })}`),
  doctors: (search?: string) => api.get<WfDoctor[]>(`${WF}/doctors${qs({ search })}`),
  createDoctor: (d: Partial<WfDoctor> & { code: string; name: string }) => api.post<WfDoctor>(`${WF}/doctors`, d),
  patchDoctor: (id: string, d: Partial<WfDoctor>) => api.patch<WfDoctor>(`${WF}/doctors/${id}`, d),
  doctorCases: (id: string) => api.get<WfCaseListResponse>(`${WF}/doctors/${id}/cases`),
  /** Everything one clinic has sent, resolved through the same group link its own portal uses. */
  clinicCases: (clinicId: string) =>
    api.get<WfCaseListResponse & { doctorCode: string; doctorId: string; unassigned: number }>(
      `${WF}/clinics/${clinicId}/cases`,
    ),

  list: (q: WfCaseListQuery) => api.get<WfCaseListResponse>(`${WF}/cases${qs(q as Record<string, unknown>)}`),
  board: (q: Partial<WfCaseListQuery> & { perStage?: number }) => api.get<WfBoardResponse>(`${WF}/cases/board${qs(q as Record<string, unknown>)}`),
  create: (c: WfCaseCreate) => api.post<WfCase>(`${WF}/cases`, c),
  get: (id: string) => api.get<WfCaseWorkspace>(`${WF}/cases/${id}`),
  patch: (id: string, p: WfCasePatch) => api.patch<WfCase>(`${WF}/cases/${id}`, p, { queue: true }),
  /** Models, aligner numbers and price - the slip that goes in the box. */
  proforma: (id: string) => api.get<WfCaseProforma>(`${WF}/cases/${id}/proforma`),
  remove: (id: string) => api.del<{ ok: boolean }>(`${WF}/cases/${id}`),

  move: (id: string, body: { toStage: string; comment?: string | null; patch?: WfCasePatch; checklistState?: Record<string, boolean>; dispatch?: Record<string, string | null> }) =>
    api.post<WfCase>(`${WF}/cases/${id}/move`, body, { queue: true }),
  assign: (id: string, body: { assignedTo?: string | null; reviewerId?: string | null }) => api.post<WfCase>(`${WF}/cases/${id}/assign`, body, { queue: true }),
  hold: (id: string, body: { reason: string; responsibleUserId?: string | null; expectedAt?: string | null; notes?: string | null }) =>
    api.post<WfCase>(`${WF}/cases/${id}/hold`, body, { queue: true }),
  resume: (id: string, body: { toStage?: string | null; comment?: string | null }) => api.post<WfCase>(`${WF}/cases/${id}/resume`, body, { queue: true }),
  cancel: (id: string, body: { reason: string; notes?: string | null }) => api.post<WfCase>(`${WF}/cases/${id}/cancel`, body),
  reopen: (id: string, body: { toStage?: string | null; comment?: string | null }) => api.post<WfCase>(`${WF}/cases/${id}/reopen`, body),
  requestApproval: (id: string, body: { kind?: WfApprovalKind; method?: string | null; comments?: string | null; simulationReady?: boolean }) =>
    api.post<WfCase>(`${WF}/cases/${id}/approvals/request`, body, { queue: true }),
  decideApproval: (
    id: string,
    body: { kind?: WfApprovalKind; decision: 'approved' | 'rejected' | 'changes_requested' | 'approved_with_instructions'; method?: string | null; comments?: string | null; reason?: string | null; evidenceFileId?: string | null; returnToStage?: string | null; nextStage?: string | null },
  ) => api.post<WfCase>(`${WF}/cases/${id}/approvals/decide`, body, { queue: true }),
  review: (id: string, body: { queue: 'mubeen_awais' | 'dr_ali'; decision: string; comment?: string | null; instructions?: string | null; nextStage?: string | null; holdReason?: string | null }) =>
    api.post<WfCase>(`${WF}/cases/${id}/reviews`, body, { queue: true }),
  production: (id: string, body: Record<string, unknown>) => api.post<{ record: unknown; case: WfCase }>(`${WF}/cases/${id}/production`, body, { queue: true }),
  patchProduction: (id: string, recordId: string, body: Record<string, unknown>) => api.patch<{ record: unknown; case: WfCase }>(`${WF}/cases/${id}/production/${recordId}`, body, { queue: true }),
  rework: (id: string, body: { kind: 'rework' | 'reprint'; reason: string; instructions?: string | null; toStage?: string | null }) => api.post<WfCase>(`${WF}/cases/${id}/rework`, body, { queue: true }),
  dispatch: (id: string, body: Record<string, unknown>) => api.post<WfCase>(`${WF}/cases/${id}/dispatch`, body, { queue: true }),
  addNote: (id: string, body: { kind: string; body: string; pinned?: boolean }) => api.post<unknown>(`${WF}/cases/${id}/notes`, body, { queue: true }),
  pinNote: (id: string, noteId: string, pinned: boolean) => api.patch<unknown>(`${WF}/cases/${id}/notes/${noteId}`, { pinned }),
  addSetupVersion: (id: string, body: { kind: string; status?: string; notes?: string | null; fileId?: string | null }) => api.post<unknown>(`${WF}/cases/${id}/setup-versions`, body),
  setSetupStatus: (id: string, vid: string, status: string) => api.patch<unknown>(`${WF}/cases/${id}/setup-versions/${vid}`, { status }),
  bulk: (body: Record<string, unknown>) => api.post<{ ok: string[]; failed: Array<{ id: string; error: string }> }>(`${WF}/cases/bulk`, { ...body, confirm: true }),

  messages: (id: string) => api.get<WfPortalMessage[]>(`${WF}/cases/${id}/messages`),
  sendMessage: (id: string, body: string) => api.post<WfPortalMessage>(`${WF}/cases/${id}/messages`, { body }),
  openRequests: () => api.get<Array<WfPortalRequest & { caseNumber: string; patientName: string }>>(`${WF}/requests`),
  resolveRequest: (id: string, status: string, resolution?: string | null) => api.post<{ ok: boolean }>(`${WF}/requests/${id}/resolve`, { status, resolution: resolution ?? null }),
  dashboard: () => api.get<WfDashboard>(`${WF}/dashboard`),
  aging: () => api.get<WfAgingReport>(`${WF}/reports/aging`),
  summary: (from: string, to: string) => api.get<WfSummaryReport>(`${WF}/reports/summary${qs({ from, to })}`),
  activity: (mine = false, limit = 100) => api.get<WfEvent[]>(`${WF}/activity${qs({ mine: mine ? 1 : undefined, limit })}`),
  notifications: (unread = false) => api.get<{ items: WfNotification[]; unread: number }>(`${WF}/notifications${qs({ unread: unread ? 1 : undefined })}`),
  readNotification: (id: string) => api.post<unknown>(`${WF}/notifications/${id}/read`),
  readAllNotifications: () => api.post<unknown>(`${WF}/notifications/read-all`),
  importCommit: (batchId: string, importDuplicates = false) => api.post<WfImportResult>(`${WF}/import/commit`, { batchId, importDuplicates }),
  importBatches: () => api.get<WfImportBatch[]>(`${WF}/import/batches`),

  // Billing. Money mutations are deliberately NOT offline-queued: a charge or a verification must
  // fail loudly and be retried by a human, never replayed silently from a queue.
  billingSummary: () => api.get<WfBillingSummary>(`${WF}/billing/summary`),
  statement: (doctorId: string, range: { from?: string; to?: string } = {}) => api.get<WfStatement>(`${WF}/billing/statement/${doctorId}${qs(range as Record<string, unknown>)}`),
  postCharge: (body: WfChargeInput) => api.post<WfLedgerEntry>(`${WF}/billing/charges`, body),
  voidEntry: (id: string, reason: string) => api.post<WfLedgerEntry>(`${WF}/billing/entries/${id}/void`, { reason }),
  /** Change a ledger line's amount (paisa); the server corrects its invoice / payment / entry. */
  editEntry: (id: string, amount: number, reason: string) =>
    api.post<{ kind: 'invoice' | 'payment' | 'entry'; from?: string; to?: string; pendingVerification?: boolean }>(`${WF}/billing/entries/${id}/edit`, { amount, reason }),
  billingPayments: (q: { status?: string; doctorId?: string } = {}) => api.get<WfPayment[]>(`${WF}/billing/payments${qs(q as Record<string, unknown>)}`),
  recordPayment: (body: Record<string, unknown>) => api.post<WfPayment>(`${WF}/billing/payments`, body),
  verifyPayment: (id: string, note?: string | null) => api.post<{ payment: WfPayment; entry: WfLedgerEntry }>(`${WF}/billing/payments/${id}/verify`, { note: note ?? null }),
  rejectPayment: (id: string, reason: string) => api.post<WfPayment>(`${WF}/billing/payments/${id}/reject`, { reason }),
  /** Undo a verification that shouldn't have happened. Reverses the statement credit and the books. */
  unverifyPayment: (id: string, reason: string) => api.post<WfPayment>(`${WF}/billing/payments/${id}/unverify`, { reason }),
  // Aligner compliance: the wear record, the photos, and the artifact shared for approval.
  compliance: (caseId: string) => api.get<CaseCompliance>(`${WF}/cases/${caseId}/compliance`),
  complianceQueue: (flagged = false) => api.get<ComplianceQueue>(`${WF}/compliance${qs({ flagged: flagged ? 1 : undefined })}`),
  /** Remove a file from a case. Soft on the server — the bytes stay for a restore. */
  deleteFile: (caseId: string, fileId: string) => api.del<{ ok: boolean }>(`${WF}/cases/${caseId}/files/${fileId}`),
  shareForApproval: (caseId: string, fileId: string, shared: boolean, note?: string | null) =>
    api.patch<WfFile>(`${WF}/cases/${caseId}/files/${fileId}/share`, { shared, note: note ?? undefined }),
  /** Clinic invoices raised against workflow cases. */
  invoices: (search?: string) => api.get<WfInvoice[]>(`${WF}/invoices${qs({ search })}`),
  invoice: (id: string) => api.get<WfInvoice>(`${WF}/invoices/${id}`),
  voidInvoice: (id: string, reason: string) => api.post<WfInvoice>(`${WF}/invoices/${id}/void`, { reason }),
  /** Issues the corrected invoice and voids this one; returns the new invoice. */
  editInvoice: (id: string, body: { lines: Array<{ description: string; quantity: number; unitAmount: number }>; notes?: string | null; reason: string }) =>
    api.post<WfInvoice>(`${WF}/invoices/${id}/edit`, body),
  backfillInvoices: () => api.post<{ created: number }>(`${WF}/invoices/backfill`, {}),

  /** The patient directory, derived from cases. */
  patients: (search?: string) => api.get<WfPatientSummary[]>(`${WF}/patients${qs({ search })}`),
  // Patient photo review queue.
  pendingPhotos: () => api.get<PendingPhoto[]>(`${WF}/photos/pending`),
  reviewPhoto: (photoId: string, status: 'approved' | 'flagged', note?: string) =>
    api.patch<{ ok: boolean }>(`${WF}/photos/${photoId}/review`, { status, note: note || undefined }),

  recordWear: (caseId: string, body: { day: string; hours: number; note?: string }) =>
    api.post<CaseCompliance>(`${WF}/cases/${caseId}/wear`, body),
  remindPatient: (caseId: string, kind: 'wear' | 'photos', message?: string) =>
    api.post<{ ok: boolean }>(`${WF}/cases/${caseId}/remind`, { kind, message: message || undefined }),

  // Treatment setups (the 3D viewer). The import is multipart, so it lives below with the other
  // FormData helpers; everything else is plain JSON.
  setups: (caseId: string) => api.get<CaseSetup[]>(`${WF}/cases/${caseId}/setups`),
  setup: (id: string) => api.get<SetupView>(`${WF}/setups/${id}`),
  shareSetup: (id: string, days = 30) => api.post<CaseSetup>(`${WF}/setups/${id}/share`, { days }),
  revokeSetupShare: (id: string) => api.post<CaseSetup>(`${WF}/setups/${id}/revoke-share`, {}),
  annotateSetup: (id: string, body: SetupAnnotationInput) => api.post<SetupAnnotation>(`${WF}/setups/${id}/annotations`, body),
  resolveSetupAnnotation: (setupId: string, annotationId: string) =>
    api.post<{ ok: boolean }>(`${WF}/setups/${setupId}/annotations/${annotationId}/resolve`),

  // Scan diary. The slots the lab offers, and the appointments clinics have taken.
  scanSlots: () => api.get<WfScanSlot[]>(`${WF}/scan/slots`),
  createScanSlot: (body: { startsAt: string; durationMin?: number; capacity?: number; location?: string | null }) =>
    api.post<WfScanSlot>(`${WF}/scan/slots`, body),
  closeScanSlot: (id: string) => api.del<{ ok: boolean }>(`${WF}/scan/slots/${id}`),
  scanBookings: () => api.get<WfScanBooking[]>(`${WF}/scan/bookings`),
  completeScanBooking: (id: string) => api.post<{ ok: boolean }>(`${WF}/scan/bookings/${id}/done`, {}),
  cancelScanBooking: (id: string, reason: string) => api.post<{ ok: boolean }>(`${WF}/scan/bookings/${id}/cancel`, { reason }),

  bankAccounts: () => api.get<WfBankAccount[]>(`${WF}/billing/accounts`),
  createBankAccount: (body: WfBankAccountInput) => api.post<WfBankAccount>(`${WF}/billing/accounts`, body),
  patchBankAccount: (id: string, body: Partial<WfBankAccountInput>) => api.patch<WfBankAccount>(`${WF}/billing/accounts/${id}`, body),
};

/**
 * Import a CAD plan export as a new revision.
 *
 * The mesh is uploaded first through the ordinary file pipeline and passed here by id, so the GLB
 * gets the same size limits, magic-byte checks and Range streaming as every other case file.
 */
export async function importSetupPlan(
  caseId: string,
  plan: File,
  opts: { meshFileId?: string | null; notes?: string } = {},
  onProgress?: (pct: number, stats?: UploadStats) => void,
): Promise<{ setup: CaseSetup; warnings: string[] }> {
  const fd = new FormData();
  const prepared = await prepareUpload(plan);
  fd.append('file', prepared.body, plan.name);
  if (prepared.gzipped) fd.append('gz', '1');
  fd.append('category', 'setup');
  if (opts.meshFileId) fd.append('meshFileId', opts.meshFileId);
  if (opts.notes) fd.append('notes', opts.notes);
  return uploadWithProgress<{ setup: CaseSetup; warnings: string[] }>(`/api${WF}/cases/${caseId}/setups`, fd, onProgress);
}

export const paymentProofUrl = (paymentId: string) => `/api${WF}/billing/payments/${paymentId}/proof`;

/** Multipart helpers (the JSON wrapper can't do FormData). */
/** Upload one file (or a .zip/.rar, which the server extracts into individual records). */
export async function uploadCaseFile(
  caseId: string,
  file: File,
  opts: { category?: string; description?: string; replacesFileId?: string } = {},
  onProgress?: (pct: number, stats?: UploadStats) => void,
): Promise<UploadOutcome> {
  const fd = new FormData();
  // Gzip first where it helps — a scan is usually the slowest thing anybody sends this lab.
  const prepared = await prepareUpload(file);
  // The name is carried on the part, so the server still sees "upper-arch.stl" and validates it as
  // one after expanding; the compression is a transport detail and nothing downstream knows.
  fd.append('file', prepared.body, file.name);
  if (prepared.gzipped) fd.append('gz', '1');
  if (opts.category) fd.append('category', opts.category);
  if (opts.description) fd.append('description', opts.description);
  if (opts.replacesFileId) fd.append('replacesFileId', opts.replacesFileId);
  return uploadWithProgress<UploadOutcome>(`/api${WF}/cases/${caseId}/files`, fd, onProgress);
}
/** A plain file returns the record; an archive returns what was extracted (and anything skipped). */
export type UploadOutcome =
  | { id: string; originalName: string; autoShared?: boolean; autoShareBlocked?: 'trays_required' | null }
  | { archive: string; extracted: Array<{ id: string; originalName: string }>; skipped: Array<{ name: string; reason: string }>; unavailable?: boolean };
export const isArchiveOutcome = (o: UploadOutcome): o is Extract<UploadOutcome, { archive: string }> => 'archive' in o;
export async function importPreview(file: File): Promise<WfImportPreview> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api${WF}/import/preview`, { method: 'POST', credentials: 'include', body: fd });
  const data = (await res.json().catch(() => null)) as (WfImportPreview & { error?: string }) | null;
  if (!res.ok || !data) throw new Error(data?.error ?? res.statusText);
  return data;
}
export const fileDownloadUrl = (caseId: string, fileId: string, inline = false) => `/api${WF}/cases/${caseId}/files/${fileId}/download${inline ? '?inline=1' : ''}`;
export const exportUrl = (q: Record<string, unknown>) => `/api${WF}/cases/export.xlsx${qs(q)}`;
export const historyExportUrl = (caseId: string) => `/api${WF}/cases/${caseId}/history.xlsx`;

/** Result of a queue-capable mutation: either the fresh case or a Deferred marker. */
export function unwrap<T>(r: T | Deferred): T | null {
  return isDeferred(r) ? null : r;
}

// ---------------------------------------------------------------------------------------------
// Config context
// ---------------------------------------------------------------------------------------------

interface WfConfigCtx extends WfConfig {
  loading: boolean;
  reload: () => Promise<void>;
  stageMap: Map<string, WfStage>;
  stageName: (key: string | null | undefined) => string;
  optionsOf: (kind: WfOptionKind, includeInactive?: boolean) => WfOption[];
  optionLabel: (kind: WfOptionKind, key: string | null | undefined) => string;
  staffName: (id: string | null | undefined) => string;
}

const Ctx = createContext<WfConfigCtx | null>(null);

export function WfConfigProvider({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<WfConfig>({ stages: [], options: [], doctors: [], staff: [] });
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    try {
      setCfg(await wfApi.config());
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  useLiveEvent<{ id: string }>('workflow.changed', (p) => {
    if (p?.id === 'config') void reload();
  });
  const value = useMemo<WfConfigCtx>(() => {
    const stageMap = new Map(cfg.stages.map((s) => [s.key, s]));
    return {
      ...cfg,
      loading,
      reload,
      stageMap,
      stageName: (k) => (k ? stageMap.get(k)?.name ?? k : '-'),
      optionsOf: (kind, includeInactive = false) => cfg.options.filter((o) => o.kind === kind && (includeInactive || o.isActive)),
      optionLabel: (kind, key) => (key ? cfg.options.find((o) => o.kind === kind && o.key === key)?.label ?? key.replace(/_/g, ' ') : '-'),
      staffName: (id) => (id ? cfg.staff.find((s) => s.id === id)?.name ?? '-' : 'Unassigned'),
    };
  }, [cfg, loading, reload]);
  return createElement(Ctx.Provider, { value }, children);
}

export function useWfConfig(): WfConfigCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useWfConfig outside WfConfigProvider');
  return v;
}
